// What a shopper loaded is written into the page's own history entry, keyed
// to the results it belongs to, so coming Back to it -- from a product they
// opened -- finds the products they had reached rather than the first page.
// The address is left as it is: every page loaded is one Back step, not one
// per page. A page the browser keeps whole in its back-forward cache needs
// none of this, and the restore only runs where the element is built afresh.
const HISTORY_KEY = "faubourgLoadMore";

class FaubourgLoadMore extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mode !== "load_more" && this.dataset.mode !== "infinite")
      return;

    this.grid = this.querySelector("[data-grid]");
    this.button = this.querySelector("[data-load-more]");
    this.status = this.querySelector("[data-load-status]");
    this.sentinel = this.querySelector("[data-more]");
    this.pagination = this.querySelector(".pagination");
    if (!this.grid || !this.button) return;

    this.loading = false;
    this.loads = 0;
    this.onClick = this.onClick.bind(this);
    this.button.addEventListener("click", this.onClick);

    this.onLeave = this.remember.bind(this);
    this.onFollow = (event) => {
      if (this.loads > 0 && event.target.closest && event.target.closest("a[href]")) this.remember();
    };
    this.grid.addEventListener("click", this.onFollow);
    if (typeof window.addEventListener === "function") window.addEventListener("pagehide", this.onLeave);

    // Arriving on page 3 from a search or a shared link, the button only goes
    // forward: the numbered pages stay, as the way back to the first two.
    if (this.pagination && !this.pagination.querySelector('[rel="prev"]')) this.pagination.hidden = true;
    this.button.hidden = false;

    const saved = this.saved();
    if (saved) this.restore(saved);
    else if (this.dataset.mode === "infinite") this.watch();
  }

  disconnectedCallback() {
    if (this.grid) this.grid.removeEventListener("click", this.onFollow);
    if (typeof window.removeEventListener === "function") window.removeEventListener("pagehide", this.onLeave);
    this.unwatch();
    if (this.controller) this.controller.abort();
    this.controller = null;
  }

  watch() {
    if (typeof IntersectionObserver !== "function") return;
    if (!this.sentinel) return;

    this.button.hidden = true;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.load(false);
        }
      },
      { rootMargin: "400px 0px" },
    );
    this.observer.observe(this.sentinel);
  }

  unwatch() {
    if (!this.observer) return;
    this.observer.disconnect();
    this.observer = null;
  }

  // The results this element holds, whichever page of them the address names.
  get key() {
    const url = new URL(window.location.href);
    url.searchParams.delete("page");
    return url.pathname + url.search;
  }

  saved() {
    const history = window.history;
    const entry = history && history.state && history.state[HISTORY_KEY];
    if (!entry || entry.key !== this.key || !(entry.loads > 0)) return null;
    return entry;
  }

  // Merged into whatever else the entry holds: the filters keep their own
  // state in the same history.
  remember() {
    const history = window.history;
    if (!history || typeof history.replaceState !== "function" || this.loads === 0) return;
    const state = Object.assign({}, history.state, {
      [HISTORY_KEY]: { key: this.key, loads: this.loads, scroll: window.scrollY || 0 },
    });
    history.replaceState(state, "");
  }

  async restore(saved) {
    this.restoring = true;
    while (this.loads < saved.loads && this.dataset.nextUrl) {
      const loaded = await this.load(false);
      if (!loaded) break;
    }
    this.restoring = false;

    // Replaced before it finished -- a filter applied, a page left -- the
    // element no longer speaks for what is on screen, and must not move it.
    if (!this.isConnected || saved.key !== this.key) return;

    if (typeof window.scrollTo === "function") window.scrollTo(0, saved.scroll || 0);
    if (this.dataset.nextUrl && this.dataset.mode === "infinite") this.watch();
  }

  onClick(event) {
    event.preventDefault();
    this.load(true);
  }

  announce(text) {
    if (this.status) this.status.textContent = text;
  }

  // A promotion the merchant placed in the grid is not a product shown.
  announceCount() {
    const template = this.dataset.showingText;
    if (!template) return;
    const shown = Array.from(this.grid.children).filter((child) => !child.hasAttribute("data-grid-promotion")).length;
    this.announce(template.replace("[count]", shown));
  }

  async load(fromClick) {
    const url = this.dataset.nextUrl;
    if (!url || this.loading) return false;

    this.loading = true;
    this.setAttribute("aria-busy", "true");
    this.button.setAttribute("disabled", "");
    if (!this.restoring) this.announce(this.dataset.loadingText || "");

    const controller = new AbortController();
    this.controller = controller;

    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set("section_id", this.dataset.sectionId);

      const response = await fetch(target.toString(), {
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(response.statusText);

      const markup = window.Faubourg.parseSection(await response.text());
      const results = markup.getElementById(this.dataset.resultsId);
      const nextHost = results && results.querySelector("faubourg-load-more");
      const nextGrid = nextHost && nextHost.querySelector("[data-grid]");
      if (!nextGrid || nextGrid.children.length === 0)
        throw new Error("section markup did not match");

      const first = nextGrid.firstElementChild;
      this.grid.append(...nextGrid.children);

      if (nextHost.dataset.nextUrl)
        this.dataset.nextUrl = nextHost.dataset.nextUrl;
      else delete this.dataset.nextUrl;

      if (!this.dataset.nextUrl) this.exhaust();
      else this.recheck();
      this.loads += 1;
      if (!this.restoring) {
        this.announceCount();
        this.remember();
      }

      if (fromClick && first) {
        const link = first.querySelector("a[href]");
        if (link) link.focus({ preventScroll: true });
      }
      return true;
    } catch (error) {
      if (error && error.name === "AbortError") return false;

      this.exhaust();
      if (this.pagination) this.pagination.hidden = false;
      this.announce("");
      return false;
    } finally {
      this.loading = false;
      this.controller = null;
      this.removeAttribute("aria-busy");
      if (this.dataset.nextUrl) this.button.removeAttribute("disabled");
    }
  }

  recheck() {
    if (!this.observer || !this.sentinel) return;
    this.observer.unobserve(this.sentinel);
    this.observer.observe(this.sentinel);
  }

  exhaust() {
    this.unwatch();
    this.button.hidden = true;
    this.button.setAttribute("disabled", "");
  }
}

if (!customElements.get("faubourg-load-more")) {
  customElements.define("faubourg-load-more", FaubourgLoadMore);
}
